import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch, batch } from 'react-redux'

import {
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorRequired,
  usePathParam,
  useTextarea
} from '@chilecompra/react-kit'
import {
  Button,
  CheckBoxSkeleton,
  CircleWarningIcon,
  Dialog,
  Skeleton,
  Textarea,
  Typography
} from '@chilecompra/react-kit/components'

import {
  onCloseDeclarationDesertedQuoteDialog,
  onDeclaretionDesertedThunk
} from './QuoteDeclarationDesertedDialog.actions'
import { BoxInput, DialogWrapper, Row, RowRadio, Strong } from './QuoteDeclarationDesertedDialog.styles'

import { topPage } from '../../modules/utils/topPage'
import { inIframe } from '../../modules/utils/inIframe'

/**
 * The DeclarationInadmissibilityQuoteDialog's container.
 */
const DeclarationDesertedQuoteDialog = () => {
  const {
    open: isOpenDeclarationDialog,
    showDeclarationInadmissibility,
    loading: declarationLoading
  } = useSelector(state => state.declarationDesertedyQuoteDialog)
  const { loading: quoteLoading } = useSelector(state => state.quoteBid)
  const {
    basicInfo: { id }
  } = useSelector(state => state.quoteBasicInformation)

  const ref = useRef()
  const dispatch = useDispatch()
  const quoteId = usePathParam('quoteId')

  const [confirmedDeclaration, setConfirmedDeclaration] = useState(false)

  const isInIframe = useMemo(() => inIframe(), [])

  const handleOnCloseDeclaration = () => {
    handleJustificationReset()
    setJustificationValue('')
    dispatch(onCloseDeclarationDesertedQuoteDialog())
  }

  useEffect(() => {
    if (showDeclarationInadmissibility) {
      setConfirmedDeclaration(false)
    }
  }, [showDeclarationInadmissibility])

  useEffect(() => {
    if (ref.current) {
      ref.current.focus()
    }
    topPage(isInIframe, isOpenDeclarationDialog)
  }, [isOpenDeclarationDialog])

  const {
    error: justificationError,
    onChange: handleJustificationChange,
    onReset: handleJustificationReset,
    setValue: setJustificationValue,
    value: justificationValue = ''
  } = useTextarea({
    errorCallbacks: [checkInputErrorMaxLength(300), checkInputErrorMinLength(5), checkInputErrorRequired()]
  })

  const disabled = justificationValue.length > 0
  const handleConfirmDeclaration = useCallback(() => {
    if (justificationValue) {
      batch(() => {
        dispatch(onDeclaretionDesertedThunk({ idSolicitudCotizacion: id, motivoDesierta: justificationValue, quoteId }))
      })
    }
  }, [justificationValue])

  return (
    <Dialog
      disableBackdropClick
      maxWidth="50%"
      open={isOpenDeclarationDialog}
      verticalAlign="top"
      onClose={handleOnCloseDeclaration}
    >
      {!confirmedDeclaration && (
        <DialogWrapper>
          <Row margin="10px 0 0">
            {quoteLoading && <Skeleton margin="0 0 0 25px" width="85%" />}
            {!quoteLoading && (
              <>
                <CircleWarningIcon fontSize="large" />
                <Typography color="black1" fontSize="18px" fontWeight="bold" lineHeight="22px" variant="h4">
                  Estás a punto de declarar desierta esta Compra Ágil
                </Typography>
              </>
            )}
          </Row>
          <Row margin="10px 0 0">
            {quoteLoading && <Skeleton margin="0 0 0 25px" width="85%" />}
            {!quoteLoading && (
              <>
                <Typography color="black1" fontSize="14px" lineHeight="18px">
                  La Compra Ágil pasará a estado desierta, la ficha y el motivo serán <br /> visibles en la plataforma.{' '}
                  <Strong>Esta acción es irreversible.</Strong>
                </Typography>
              </>
            )}
          </Row>

          <Row margin="10px 0 0">
            <RowRadio>
              {declarationLoading && (
                <>
                  <CheckBoxSkeleton width="85%" />
                </>
              )}
              {!declarationLoading && (
                <>
                  <Typography color="black1" fontSize="14px" lineHeight="18px">
                    <Strong>Motivo</Strong>
                  </Typography>
                  <BoxInput>
                    <Textarea
                      inputRef={ref}
                      error={justificationError}
                      label=""
                      margin="10px 0 0 0"
                      maxCount={300}
                      multiline
                      onChange={handleJustificationChange}
                      value={justificationValue}
                      width="70%"
                    />
                  </BoxInput>
                </>
              )}
            </RowRadio>
          </Row>

          <Row>
            <Button
              color="error"
              type="button"
              onClick={handleConfirmDeclaration}
              disabled={justificationError.length > 0 || !disabled}
            >
              Declarar desierta
            </Button>
          </Row>
          <Row margin="20px 0 0">
            <Button variant="text" onClick={handleOnCloseDeclaration}>
              Cerrar
            </Button>
          </Row>
        </DialogWrapper>
      )}
    </Dialog>
  )
}

export default DeclarationDesertedQuoteDialog
